import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject, delay, of, switchMap, filter } from 'rxjs';
import { environment } from '../../environments/environment';
import { LOADING_ELEMENT } from '../shared/components/loading-spinner/loading-element.token';
import { ExternalLeadHeaderModel, ExternalLeadInstructionModel } from '../views/external-lead-header.view';
import { RequiredFields } from '../views/requiredFields.view';
import { ExternalReferringSource } from '../views/external-referringSource.view';
import { StateNames } from '../views/statename.view';
import { StateCounty } from '../views/state-county.view';
import { InvolvedParty } from '../views/involved-party.view';
import { SubjectRequiredFieldsModel } from '../views/subject-required-fields-model.view';
import { ExternalReferralLeadReason } from '../views/external-referral-reason.view';
import { CreateLeadResponse } from '../views/create-lead-response.view';
import { ExternalLeadConfirmationModel } from '../views/external-lead-confirmation.view';

@Injectable({
  providedIn: 'root'
})
export class VendorService {

  baseUrl = environment.baseUri;

  private fileUploadStartSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private fileUploadEndSubject: Subject<any> = new Subject<any>();
  private fileUploadDataSubject: Subject<any> = new Subject<any>();
  uploadFiles: string[] = [];
  private storageKey = 'activeformdata';
  private dataSubject = new Subject<any>();
  filesReadyForUpload: any = [];
  FilesToUpload: any = [];

  constructor(private http: HttpClient) { }

  public getPayorIdByCode(securityCode: string | null): Observable<number> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<number>(this.baseUrl + "PublicFacingReferral/GetPayorIdByCode?securityCode=" + securityCode, { context: contextDetails });
  }

  public verifyReferralCode(securityCode: string | null): Observable<number> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<number>(this.baseUrl + "PublicFacingReferral/VerifyReferralCode?securityCode=" + securityCode, { context: contextDetails });
  }

  public getLeadHeaders(payorkey: number): Observable<ExternalLeadHeaderModel> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ExternalLeadHeaderModel>(this.baseUrl + "PublicFacingReferral/GetLeadHeaders?payorKey=" + payorkey, { context: contextDetails });
  }

  public getRequiredFields(payorkey: number): Observable<RequiredFields> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<RequiredFields>(this.baseUrl + "PublicFacingReferral/GetRequiredFields?payorKey=" + payorkey, { context: contextDetails });
  }

  public getReferringSourcesLookup(payorkey: number): Observable<ExternalReferringSource[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ExternalReferringSource[]>(this.baseUrl + "PublicFacingReferral/GetReferringSourcesLookup?payorKey=" + payorkey, { context: contextDetails });
  }

  public getStateNames(): Observable<StateNames[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<StateNames[]>(this.baseUrl + "PublicFacingReferral/GetStates", { context: contextDetails });
  }

  public getLeadQuestion(payorkey: number): Observable<any> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<any>(this.baseUrl + "PublicFacingReferral/GetLeadQuestion?payorKey=" + payorkey, { context: contextDetails });
  }

  public getStateCounties(isocode: string): Observable<StateCounty[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<StateCounty[]>(this.baseUrl + "PublicFacingReferral/GetStateCounties?isocode=" + isocode, { context: contextDetails });
  }

  public getInvolvedPartiesLookup(payorkey: number): Observable<InvolvedParty[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InvolvedParty[]>(this.baseUrl + "PublicFacingReferral/GetInvolvedPartiesLookup?payorKey=" + payorkey, { context: contextDetails });
  }

  public getSubjectRequiredFieldsByInvolvedParty(internalInvolvedPartiesKey: number, payorkey: number): Observable<SubjectRequiredFieldsModel[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<SubjectRequiredFieldsModel[]>(this.baseUrl + "PublicFacingReferral/GetSubjectRequiredFieldsByInvolvedParty?payorKey=" + payorkey + "&internalInvolvedPartiesKey=" + internalInvolvedPartiesKey, { context: contextDetails });
  }

  public getReferralTypesLookup(payorkey: number): Observable<ExternalReferralLeadReason[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ExternalReferralLeadReason[]>(this.baseUrl + "PublicFacingReferral/GetReferralTypesLookup?payorKey=" + payorkey, { context: contextDetails });
  }

  public createLead(payorkey: number, data: any): Observable<CreateLeadResponse> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.post<CreateLeadResponse>(this.baseUrl + "PublicFacingReferral/CreateLead?payorKey=" + payorkey, data, { context: contextDetails });
  }

  sendUploadStartData(data: any) {
    this.fileUploadStartSubject.next(data);
  }

  getUploadStartData(): Observable<any> {
    return this.fileUploadStartSubject.asObservable().pipe(
      filter((data: any) => data !== null)
    );
  }

  sendUploadEndData(data: any) {
    this.fileUploadEndSubject.next(data);
  }

  getUploadEndData(): Observable<any> {
    return this.fileUploadEndSubject.asObservable();
  }

  sendUploadFileData(data: any) {
    this.fileUploadDataSubject.next(data);
  }

  getUploadFileData(): Observable<any> {
    this.fileUploadDataSubject.observers = [];
    return this.fileUploadDataSubject.asObservable();
  }

  public addAttachmentToDB(data: any | null): Observable<number> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.post<number>(this.baseUrl + "PublicFacingReferral/AddAttachmentToDB", data, { context: contextDetails });
  }

  public commitLargeFiles(attachmentKey: any | null, fileName: any, totalChunks: any): Observable<number> {
    return this.http.get<number>(this.baseUrl + "PublicFacingReferral/CommitFile?attachmentKey=" + attachmentKey + "&fileName=" + fileName + "&totalChunks=" + totalChunks);
  }

  public GetAttachmentStatus(data: any | null): Observable<any> {
    return this.http.post<any>(this.baseUrl + "PublicFacingReferral/GetAttachmentStatus", data);
  }

  public pollStatus(interval: number, data: any | null): Observable<any> {
    return this.GetAttachmentStatus(data).pipe(switchMap(status => {
      if (status.filter((x: any) => x.VirusScanStatus == null || x.VirusScanStatus == 0).length == 0) {
        return of(status);
      }
      else {
        return of(status).pipe(delay(interval), switchMap(() => this.pollStatus(interval, data)));
      }
    }));
  }

  public getLeadInstructions(payorkey: number): Observable<ExternalLeadInstructionModel> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ExternalLeadInstructionModel>(this.baseUrl + "PublicFacingReferral/GetInstructions?payorKey=" + payorkey, { context: contextDetails });
  }

  public getLeadConfirmation(payorkey: number): Observable<ExternalLeadConfirmationModel> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ExternalLeadConfirmationModel>(this.baseUrl + "PublicFacingReferral/GetLeadConfirmation?payorKey=" + payorkey, { context: contextDetails });
  }
  public getReferenceId(caseActivityKey: number): Observable<string> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get(this.baseUrl + "PublicFacingReferral/GetReferenceIdByCaseActivityKey?caseActivityKey=" + caseActivityKey, {
      context: contextDetails, responseType: 'text'
    });
  }

  public sendReferralEmail(data:any): Observable<any> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.post<any>(this.baseUrl + "PublicFacingReferral/SendReferralEmail", data, { context: contextDetails });
  }

}
